
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star } from 'lucide-react';
import { Activity } from '@/types';
import { cn } from '@/lib/utils';

interface HomeActivityCardProps {
  activity: Activity;
  className?: string;
}

const HomeActivityCard: React.FC<HomeActivityCardProps> = ({ activity, className }) => {
  const minPrice = activity.priceTiers?.length
    ? Math.min(...activity.priceTiers.map((t) => t.pricePerPerson))
    : null;

  const categoryLabel =
    typeof activity.category === 'object' && activity.category !== null
      ? (activity.category as { name?: string }).name ?? ''
      : String(activity.category ?? '');

  return (
    <Link
      to={`/activity/${activity.slug}`}
      className={cn(
        'group bg-white rounded-xl overflow-hidden shadow-brand-sm hover:shadow-brand-md hover:-translate-y-0.5 transition-all duration-250 flex flex-col no-underline text-inherit',
        className
      )}
    >
      {/* Image area */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-brand-dark to-brand-secondary transition-transform duration-500 group-hover:scale-105"
          style={activity.mainImage ? { backgroundImage: `url(${activity.mainImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        />
        {/* Decorative large emoji */}
        <span className="absolute -right-8 -bottom-8 text-[140px] opacity-35 -rotate-12 select-none pointer-events-none" style={{ filter: 'drop-shadow(0 6px 16px rgba(0,0,0,.3))' }}>
          {getCategoryEmoji(String(activity.category))}
        </span>

        {/* Category pill */}
        {categoryLabel && (
          <span className="absolute top-3 left-3 bg-brand-primary text-white rounded-pill px-3 py-1 text-xs font-semibold">
            {categoryLabel}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-heading font-bold text-[18px] text-brand-text group-hover:text-brand-primary transition-colors mb-1.5">
          {activity.name}
        </h3>

        <div className="flex items-center gap-1.5 text-[13px] text-brand-text-muted mb-2.5">
          <MapPin size={14} className="flex-shrink-0" />
          <span>{activity.city}</span>
        </div>

        {activity.rating > 0 && (
          <div className="flex items-center gap-1.5 text-[13px] text-brand-text mb-3.5">
            <span className="text-brand-accent">{'★'.repeat(Math.round(activity.rating))}</span>
            <span className="font-medium">{activity.rating.toFixed(1)}</span>
            {activity.reviewCount > 0 && (
              <span className="text-brand-text-subtle">({activity.reviewCount} avis)</span>
            )}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-3 border-t border-brand-divider">
          <div>
            {minPrice !== null ? (
              <>
                <span className="font-heading font-bold text-[17px] text-brand-text">{minPrice.toFixed(2).replace('.', ',')}€</span>
                <span className="block text-[12px] text-brand-text-subtle font-medium">par personne</span>
              </>
            ) : (
              <span className="text-sm text-brand-text-muted">Sur demande</span>
            )}
          </div>
          <span className="bg-brand-primary text-white rounded-lg px-4 py-3 text-[13px] font-semibold active:scale-95 transition-transform" style={{ boxShadow: '0 4px 12px rgba(255,107,53,.22)' }}>
            Réserver
          </span>
        </div>
      </div>
    </Link>
  );
};

function getCategoryEmoji(category: string): string {
  const map: Record<string, string> = {
    bowling: '🎳', karting: '🏎️', 'escape-game': '🔐', 'laser-game': '🔫',
    padel: '🎾', 'foot-indoor': '⚽', 'trampoline-park': '🤸', billard: '🎱',
    'paintball-indoor': '🎨', 'vr-arcade': '🥽', 'quiz-room': '🧩', autre: '✨',
  };
  return map[category] ?? '🎯';
}

export default HomeActivityCard;
